import { IFolderViewsEnrichment } from '@app/library-module/core/types/folder'

export enum LibraryItemAccessType {
  OWNER = 'owner',
  ORGANIZATION = 'organization',
  PRIVATE_ACCESS = 'private_access',
}

export enum LibraryItemPublicAccessType {
  ANYONE = 'anyone',
  EMAIL_LIST = 'email_list',
}

export enum LibraryFolderPublicAccessType {
  ANYONE = 'anyone',
  ORGANIZATION = 'organization',
}

export enum AutoDeletionUnit {
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

export interface IChangeAccessDataEvent {
  private_access_type: LibraryItemAccessType
  private_access_grants: ILibraryPrivateAccessGrantCreate[]
  owner_id: number
}

export interface ICreateFolderEvent {
  type: LibraryItemAccessType
  publicAccessType: LibraryFolderPublicAccessType
  privateAccessGrants: ILibraryPrivateAccessGrantCreate[]
  userWithAccessIds: number[]
  teamWithAccessIds: number[]
  departmentWithAccessIds: number[]
  userWithDefaultFolderIds: number[]
  ownerId: number
  title: string
  publicDownloadsEnabled: boolean
  publicCommentsEnabled: boolean
  autoDeletion: IUploadAutoDeletion | null
  transcribation: IChangeFolderTranscription
}

export interface IChangeFolderData {
  id: number
  uuid: string
  type: LibraryItemAccessType
  publicAccessType: LibraryFolderPublicAccessType
  privateAccessGrants: ILibraryPrivateAccessGrant[]
  userWithDefaultFolderIds: number[]
  ownerId: number
  title: string
  publicDownloadsEnabled: boolean
  publicCommentsEnabled: boolean
  autodeleteValue: null | number
  autodeleteUnit: null | AutoDeletionUnit
  analytics: IChangeFolderAnalytics
  transcribation: IChangeFolderTranscription
  roistatEnabled: boolean
}

export interface IChangeUploadAccessData {
  uuid: string
  type: LibraryItemAccessType
  privateAccessGrants: ILibraryPrivateAccessGrant[]
  ownerId: number
}

export interface IUploadAutoDeletion {
  value: number
  unit: AutoDeletionUnit
}

export interface IChangeFolderAnalytics {
  analyticsEnabled: boolean
  trackStartView: boolean
  trackTenPercent: boolean
  trackFiftyPercent: boolean
  trackFinishView: boolean
  trackLeadFormSubmitted: boolean
  startViewGoal: string | null
  tenPercentGoal: string | null
  fiftyPercentGoal: string | null
  finishViewGoal: string | null
  leadFormSubmittedGoal: string | null
}

export interface IFolderAnalyticsParams {
  analytics_enabled: boolean
  track_start_view: boolean
  track_ten_percent: boolean
  track_fifty_percent: boolean
  track_finish_view: boolean
  track_lead_form_submitted: boolean
  start_view_goal: string | null
  ten_percent_goal: string | null
  fifty_percent_goal: string | null
  finish_view_goal: string | null
  lead_form_submitted_goal: string | null
}

export interface IChangeFolderTranscription {
  transcriptionsEnabled: boolean
  diarizationEnabled: boolean
  summaryEnabled: boolean
  summaryUserPrompt: string | null
  tasksExtractionEnabled: boolean
  tasksUserPrompt: string | null
}

export interface IFolderTranscriptionParams {
  transcriptions_enabled: boolean
  diarization_enabled: boolean
  summary_enabled: boolean
  summary_user_prompt: string | null
  tasks_extraction_enabled: boolean
  tasks_user_prompt: string | null
}

export interface IReplaceRecordPayload {
  target_url: string
}

export interface IJoinRecordPayload {
  target_url: string
  merge_type: RecordMergeType
}

export interface IJoinRecordResponse {
  new_upload_url: string
}

export enum RecordMergeType {
  BEFORE = 'before',
  AFTER = 'after',
}

export enum LibraryGranteeType {
  USER = 'user',
  TEAM = 'team',
  DEPARTMENT = 'department',
  OWNER = 'owner',
  // Это системные типы, они не приходят из API
  // ALL_ORG_USERS = 'all_org_users',
  // ACCESS_ALL_UPLOADS = 'access_all_uploads',
  // ACCESS_ALL_FOLDERS = 'access_all_folders',
}

export interface ILibraryPrivateAccessPermissions {
  can_watch: boolean
  can_edit: boolean
  can_delete: boolean
  can_duplicate: boolean
  can_download: boolean
  can_manage_access: boolean
  can_access_analytics: boolean
  can_access_interview_submissions: boolean
  can_access_lead_forms: boolean
  can_manage_links: boolean
}
export type LibraryPrivateAccessPermissionKey = keyof ILibraryPrivateAccessPermissions

export const EMPTY_LIBRARY_PRIVATE_ACCESS_PERMISSIONS: ILibraryPrivateAccessPermissions = {
  can_watch: false,
  can_edit: false,
  can_delete: false,
  can_duplicate: false,
  can_download: false,
  can_manage_access: false,
  can_access_analytics: false,
  can_access_interview_submissions: false,
  can_access_lead_forms: false,
  can_manage_links: false,
}

export const FULL_LIBRARY_PRIVATE_ACCESS_PERMISSIONS: ILibraryPrivateAccessPermissions = {
  can_watch: true,
  can_edit: true,
  can_delete: true,
  can_duplicate: true,
  can_download: true,
  can_manage_access: true,
  can_access_analytics: true,
  can_access_interview_submissions: true,
  can_access_lead_forms: true,
  can_manage_links: true,
}

export interface ILibraryPrivateAccessGrantBase extends ILibraryPrivateAccessPermissions {
  grantee_type: LibraryGranteeType
  grantee_id: number
}

export interface ILibraryPrivateAccessGrantCreate extends ILibraryPrivateAccessGrantBase {
  grantee_id: number
}

export interface ILibraryAccessSettingsValue {
  type: LibraryItemAccessType
  selectedUserIds: number[]
  selectedTeamIds: number[]
  selectedDepartmentIds: number[]
  grants: ILibraryPrivateAccessGrantCreate[]
}

export const EMPTY_LIBRARY_ACCESS_SETTINGS_VALUE: ILibraryAccessSettingsValue = {
  type: LibraryItemAccessType.OWNER,
  selectedUserIds: [],
  selectedTeamIds: [],
  selectedDepartmentIds: [],
  grants: [],
}

export interface ILibraryPrivateAccessGrant extends ILibraryPrivateAccessGrantBase {
  id: number
}
